// Progress evidence: tick the files (photos, dockets, reports) that go into the one-PDF certificate pack; drop new proof.
import { useState } from 'react';
import { write } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { fmtDateTime } from '../../lib/format';
import type { FileRow } from '../ItemFiles';
import { errText, useToast } from '../kit';
import { useViewer } from '../Viewer';

const PACKABLE = /^(application\/pdf|image\/(jpeg|png))$/i;

export function Evidence({ itemId, siteId, files, canEdit, onChanged }: { itemId: string; siteId: string; files: (FileRow & { in_pack?: boolean })[]; canEdit: boolean; onChanged: () => void }) {
  const toast = useToast();
  const viewer = useViewer();
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const list = files.filter((f) => f.is_current && f.kind === 'file' && !f.deleted_at);
  const ticked = list.filter((f) => f.in_pack);

  async function tick(f: FileRow & { in_pack?: boolean }, v: boolean) {
    try {
      await write('files', 'update', { id: f.id, values: { in_pack: v }, summary: `${v ? 'Added to' : 'Removed from'} the certificate pack: ${f.original_name}` });
      onChanged();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function add(fl: FileList | File[]) {
    for (const f of Array.from(fl)) {
      setBusy(`Uploading ${f.name}…`);
      try {
        const r = await uploadFile(f, { siteId, linkedTable: 'items', linkedId: itemId, notes: 'Progress evidence' });
        const id = (r.row as { id?: string }).id;
        if (id && PACKABLE.test(String((r.row as { mime?: string }).mime ?? f.type))) await write('files', 'update', { id, values: { in_pack: true }, summary: `Added to the certificate pack: ${f.name}` });
      } catch (e) {
        toast({ tone: 'error', text: `${f.name}: ${errText(e)}` });
      }
    }
    setBusy(null);
    onChanged();
  }

  return (
    <div>
      {list.length === 0 ? <p className="muted small">No files yet.</p> : null}
      <div className="ev-grid">
        {list.map((f) => {
          const can = PACKABLE.test(f.mime ?? '');
          return (
            <div key={f.id} className={`ev ${f.in_pack ? 'on' : ''}`}>
              <label className="ev-tick" title={can ? 'Include in the certificate pack' : 'Only PDFs and JPG/PNG photos can go into the pack'}>
                <input type="checkbox" checked={Boolean(f.in_pack)} disabled={!canEdit || !can} onChange={(e) => void tick(f, e.target.checked)} aria-label={`Include ${f.original_name} in the certificate pack`} />
              </label>
              <button type="button" className="ev-name linkbtn" onClick={() => viewer({ id: f.id, name: f.original_name, mime: f.mime, kind: f.kind }, list.map((x) => ({ id: x.id, name: x.original_name, mime: x.mime, kind: x.kind })))}>
                {f.original_name}
              </button>
              <span className="muted small">{fmtDateTime(f.uploaded_at ?? f.created_at)}</span>
            </div>
          );
        })}
      </div>
      {canEdit ? (
        <div
          className={`drop ${over ? 'over' : ''}`}
          onDragOver={(e) => (e.preventDefault(), setOver(true))}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            if (e.dataTransfer.files.length) void add(e.dataTransfer.files);
          }}
        >
          {busy ?? (
            <>
              Drop photos, delivery dockets or test reports here — or{' '}
              <label className="linkbtn">
                choose files
                <input type="file" multiple hidden accept="image/*,application/pdf" capture={undefined} onChange={(e) => e.target.files && void add(e.target.files)} />
              </label>
            </>
          )}
        </div>
      ) : null}
      <p className="small" style={{ margin: '8px 0 0' }}>
        <strong>{ticked.length} ticked</strong> go into the certificate pack — one PDF: certificate page, line schedule, then the evidence, then the comment register.
      </p>
    </div>
  );
}
